import { useToast } from "../../ui";
import {
  getPushPermission,
  isPushSupported,
  sendTestAdminPush,
  subscribeToAdminPush,
  unsubscribeFromAdminPush,
} from "../../../utils/pushNotifications";
import { Button } from "../../ui";
import { useEffect, useState } from "react";
import aa from "./adminApp.module.css";

export const ROLE_LABEL = {
  owner: "Владелец",
  admin: "Администратор",
  trainer: "Тренер",
};

export function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(n);
}

export function formatDateRu(iso) {
  if (!iso) return "—";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function AdminPushBlock() {
  const toast = useToast();
  const [permission, setPermission] = useState("default");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isPushSupported()) getPushPermission().then(setPermission);
    else setPermission("unsupported");
  }, []);

  if (permission === "unsupported") {
    return <p className={aa.ownerProfileHint}>Push недоступен в этом браузере.</p>;
  }

  const enabled = permission === "granted";

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await unsubscribeFromAdminPush();
        setPermission(Notification.permission);
        toast.success("Push отключены");
      } else {
        await subscribeToAdminPush();
        setPermission("granted");
        toast.success("Push включены");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await sendTestAdminPush();
      toast.success("Тестовое уведомление отправлено");
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={aa.ownerPushBlock}>
      <p className={aa.ownerProfileHint}>
        {enabled
          ? "Напоминания о платежах клиентов, новых заявках и изменениях в клубе."
          : "Включите уведомления, чтобы не пропускать важные события."}
      </p>
      <div className={aa.ownerPushActions}>
        <Button
          type="button"
          onClick={toggle}
          disabled={busy}
          variant={enabled ? "secondary" : "primary"}
        >
          {busy ? "…" : enabled ? "Отключить" : "Включить push"}
        </Button>
        {enabled ? (
          <Button type="button" variant="ghost" onClick={test} disabled={busy}>
            Тест
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function billingToneClass(billing) {
  if (billing?.is_expiring_soon) return aa.ownerBillingWarn;
  if (billing?.is_paid) return aa.ownerBillingOk;
  return aa.ownerBillingDanger;
}

export function BillingSummaryCard({ billing, formatRub, formatDateRu, onDetails }) {
  return (
    <button
      type="button"
      className={`${aa.ownerBillingCard} ${aa.staffProfileBillingSummary} ${billingToneClass(billing)}`}
      onClick={onDetails}
    >
      <div className={aa.ownerBillingHead}>
        <div>
          <span className={aa.ownerBillingEyebrow}>Подписка CRM</span>
          <h3 className={aa.ownerBillingPlan}>
            {billing.plan_label}
            <span className={aa.ownerBillingStatus}>{billing.status_label}</span>
          </h3>
        </div>
        <div className={aa.ownerBillingPrice}>
          <strong>{formatRub(billing.payment_amount_rub)} ₽</strong>
          <span>за {billing.billing_period_label}</span>
        </div>
      </div>
      <div className={aa.staffProfileBillingRow}>
        <span>Оплачено до {formatDateRu(billing.paid_until)}</span>
        <span>
          {billing.days_left != null
            ? billing.days_left < 0
              ? "истекла"
              : `осталось ${billing.days_left} дн.`
            : ""}
        </span>
      </div>
      <span className={aa.staffProfileBillingLink}>Подробнее →</span>
    </button>
  );
}

export function BillingDetailsCard({
  billing,
  payBusy,
  onPay,
  trialDays,
  formatRub,
  formatDateRu,
}) {
  const billingTone = billing?.is_expiring_soon
    ? aa.ownerBillingWarn
    : billing?.is_paid
      ? aa.ownerBillingOk
      : aa.ownerBillingDanger;

  return (
    <div className={`${aa.ownerBillingCard} ${billingTone}`}>
      <div className={aa.ownerBillingHead}>
        <div>
          <span className={aa.ownerBillingEyebrow}>Подписка CRM</span>
          <h3 className={aa.ownerBillingPlan}>
            {billing.plan_label}
            <span className={aa.ownerBillingStatus}>{billing.status_label}</span>
          </h3>
        </div>
        <div className={aa.ownerBillingPrice}>
          <strong>{formatRub(billing.payment_amount_rub)} ₽</strong>
          <span>за {billing.billing_period_label}</span>
        </div>
      </div>
      <div className={aa.ownerBillingGrid}>
        <div className={aa.ownerBillingCell}>
          <span className={aa.ownerBillingCellLabel}>Оплачено до</span>
          <span className={aa.ownerBillingCellValue}>
            {formatDateRu(billing.paid_until)}
          </span>
        </div>
        <div className={aa.ownerBillingCell}>
          <span className={aa.ownerBillingCellLabel}>Осталось</span>
          <span className={aa.ownerBillingCellValue}>
            {billing.days_left != null
              ? billing.days_left < 0
                ? "истекла"
                : `${billing.days_left} дн.`
              : "—"}
          </span>
        </div>
        <div className={aa.ownerBillingCell}>
          <span className={aa.ownerBillingCellLabel}>Ежемесячно</span>
          <span className={aa.ownerBillingCellValue}>
            {formatRub(billing.monthly_price_rub)} ₽
          </span>
        </div>
        <div className={aa.ownerBillingCell}>
          <span className={aa.ownerBillingCellLabel}>Клуб</span>
          <span className={aa.ownerBillingCellValue}>
            {billing.subdomain || "—"}
          </span>
        </div>
      </div>
      {!billing.is_paid ? (
        <p className={aa.ownerBillingNote}>
          {billing.is_demo_expired || billing.is_trial
            ? `Демо-доступ закончился. Оплатите подписку (${formatRub(billing.payment_amount_rub)} ₽ / ${billing.billing_period_label}), чтобы продолжить работу.`
            : "Подписка не активна. Оплатите онлайн или свяжитесь с поддержкой BaySport."}
        </p>
      ) : billing.is_trial ? (
        <p className={aa.ownerBillingNote}>
          Демо-доступ активен — осталось {billing.days_left ?? "—"} дн. из{" "}
          {billing.trial_days || trialDays}. После окончания потребуется оплата.
        </p>
      ) : billing.is_expiring_soon ? (
        <p className={aa.ownerBillingNote}>Скоро потребуется продление подписки.</p>
      ) : null}
      <div style={{ marginTop: 12 }}>
        <Button type="button" disabled={payBusy} onClick={onPay}>
          {payBusy ? "…" : `Оплатить ${formatRub(billing.payment_amount_rub)} ₽`}
        </Button>
      </div>
    </div>
  );
}
